import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

/**
 * WelcomeTour — 5-step spotlight overlay tour for first-time users.
 * Highlights key features with tooltips positioned next to target elements.
 */
const TOUR_STEPS = [
  {
    target: '[data-testid="search-input"]',
    fallback: '.search-bar',
    title: 'Search Anything',
    description: 'Find any page, feature, or setting instantly. Press Enter to go.',
    position: 'bottom',
  },
  {
    target: '[data-testid="notifications-button"]',
    title: 'Notifications',
    description: 'See alerts, friend requests, and platform updates. The badge shows unread count.',
    position: 'bottom-end',
  },
  {
    target: '[data-testid="fixed-cards-grid"]',
    title: 'Your Dashboard Cards',
    description: 'These are your key activity cards. Click "Customize" to add or remove cards.',
    position: 'top',
  },
  {
    target: '[data-testid="customize-dashboard-btn"]',
    title: 'Customise Your View',
    description: 'Add optional cards like Badges, Social Score, and Mood Insights to personalise your dashboard.',
    position: 'bottom',
  },
  {
    target: '[data-testid="profile-button"]',
    title: 'Your Profile',
    description: 'Access Settings, change themes, share HiBFF with friends, or log out.',
    position: 'bottom-end',
  },
];

const WelcomeTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(true);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    const step = TOUR_STEPS[currentStep];
    const el = document.querySelector(step.target) || (step.fallback ? document.querySelector(step.fallback) : null);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [updatePosition]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleFinish = () => {
    setVisible(false);
    localStorage.setItem('hibff_welcome_tour_done', 'true');
    if (onComplete) onComplete();
  };

  if (!visible) return null;

  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;

  // Calculate tooltip position — on phones, pin as a bottom sheet instead of
  // floating beside the target (which overlaps content on small screens).
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  let tooltipStyle = {};
  if (isMobile) {
    tooltipStyle = { bottom: 16, left: 16, right: 16 };
  } else if (targetRect) {
    const pos = step.position || 'bottom';
    if (pos === 'bottom' || pos === 'bottom-end') {
      tooltipStyle = {
        top: targetRect.bottom + 12,
        left: pos === 'bottom-end' ? Math.max(16, targetRect.right - 320) : Math.max(16, targetRect.left),
      };
    } else if (pos === 'top') {
      tooltipStyle = {
        bottom: window.innerHeight - targetRect.top + 12,
        left: Math.max(16, targetRect.left),
      };
    }
  } else {
    tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return (
    <div className="fixed inset-0 z-[70]" data-testid="welcome-tour">
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={handleFinish} />

      {/* Spotlight cutout */}
      {targetRect && (
        <div
          className="absolute border-2 border-orange-400 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] z-[71]"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-[72] w-auto sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-orange-600 font-bold uppercase tracking-wide">
              Step {currentStep + 1} of {TOUR_STEPS.length}
            </span>
            <button onClick={handleFinish} className="text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">{step.title}</h3>
          <p className="text-sm text-gray-600">{step.description}</p>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100">
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <div className="flex gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === currentStep ? 'bg-orange-500' : 'bg-gray-300'}`} />
            ))}
          </div>
          <button
            onClick={handleNext}
            className="flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700"
            data-testid="tour-next-btn"
          >
            {isLast ? 'Done' : 'Next'} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
